import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function Meals() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [meals, setMeals] = useState([]);
  const [loadingMeals, setLoadingMeals] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('All'); // All, Break, Lunch, Snack
  const [filterFrequency, setFilterFrequency] = useState('All'); // All, Daily, Weekly, Monthly
  const [editingMeal, setEditingMeal] = useState(null);
  const [editForm, setEditForm] = useState({
    meal_name: '',
    meal_type: '',
    frequency: '',
    description: ''
  });
  const [editErrors, setEditErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setFilterType('All');
    setFilterFrequency('All');
    setMeals([]);
  }, [selectedBranchId]);

  const fetchMeals = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingMeals(true);
      let query = supabase
        .from('meals')
        .select('*')
        .eq('school_id', schoolData.id);
      
      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }
      
      // Apply filters
      if (filterType !== 'All') {
        query = query.eq('meal_type', filterType);
      }
      
      if (filterFrequency !== 'All') {
        query = query.eq('frequency', filterFrequency);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching meals:', error);
        setError('Failed to load meals');
        return;
      }
      
      setMeals(data || []);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load meals');
    } finally {
      setLoadingMeals(false);
    }
  };

  useEffect(() => {
    if (schoolData?.id) {
      fetchMeals();
    }
  }, [schoolData?.id, filterType, filterFrequency, selectedBranchId]);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return '-';
    }
  };

  const getMealTypeIcon = (type) => {
    switch(type) {
      case 'Break': return '🌅';
      case 'Lunch': return '🍽️';
      case 'Snack': return '🍎';
      default: return '🍽️';
    }
  };

  const getFrequencyBadgeColor = (frequency) => {
    switch(frequency) {
      case 'Daily': return '#10B981'; // green
      case 'Weekly': return '#3B82F6'; // blue
      case 'Monthly': return '#F59E0B'; // amber
      default: return '#6B7280'; // gray
    }
  };

  const openEditPopup = (meal) => {
    setEditingMeal(meal);
    setEditForm({
      meal_name: meal.meal_name || '',
      meal_type: meal.meal_type || '',
      frequency: meal.frequency || '',
      description: meal.description || ''
    });
    setEditErrors({});
  };

  const closeEditPopup = () => {
    setEditingMeal(null);
    setEditForm({
      meal_name: '',
      meal_type: '',
      frequency: '',
      description: ''
    });
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.meal_name.trim()) {
      errors.meal_name = 'Meal name is required';
    }
    
    if (!editForm.meal_type.trim()) {
      errors.meal_type = 'Meal type is required';
    }
    
    if (!editForm.frequency.trim()) {
      errors.frequency = 'Frequency is required';
    }
    
    if (!editForm.description.trim()) {
      errors.description = 'Description is required';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const mealData = {
        meal_name: editForm.meal_name.trim(),
        meal_type: editForm.meal_type,
        frequency: editForm.frequency,
        description: editForm.description.trim(),
        updated_at: new Date().toISOString()
      };
      
      let updateQuery = supabase
        .from('meals')
        .update({
          ...mealData,
          branch_id: selectedBranchId
        })
        .eq('id', editingMeal.id);

      if (selectedBranchId != null) {
        updateQuery = updateQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await updateQuery;
      
      if (error) {
        console.error('Error updating meal:', error);
        alert(`Failed to update meal: ${error.message}`);
        setIsSubmitting(false);
        return;
      }
      
      // Refresh meals list
      await fetchMeals();
      
      // Close popup
      closeEditPopup();
      
      alert('Meal updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update meal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMeal = async (mealId, mealName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${mealName}"?\n\nThis action cannot be undone.`
    );
    
    if (!confirmDelete) {
      return;
    }

    try {
      let deleteQuery = supabase
        .from('meals')
        .delete()
        .eq('id', mealId);

      if (selectedBranchId != null) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await deleteQuery;
      
      if (error) {
        console.error('Error deleting meal:', error);
        alert('Failed to delete meal. Please try again.');
        return;
      }
      
      alert('Meal deleted successfully!');
      await fetchMeals();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete meal. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Meals</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="branch-filter-row">
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <span className="branch-context-text">
              {selectedBranchId
                ? 'Filtering meals for the selected branch'
                : 'Showing meals for all branches'}
            </span>
          </div>
          <div className="students-content">
            <div className="students-header">
              <h1>Meals Management</h1>
              <p>View and manage all meals in your school</p>
            </div>

            <div className="table-toolbar">
              <select 
                className="table-select" 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="All">All Types</option>
                <option value="Break">Break</option>
                <option value="Lunch">Lunch</option>
                <option value="Snack">Snack</option>
              </select>

              <select 
                className="table-select" 
                value={filterFrequency} 
                onChange={(e) => setFilterFrequency(e.target.value)}
              >
                <option value="All">All Frequencies</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>

            <div className="students-stats">
              <div className="stat-item">
                <span className="stat-number">{meals.length}</span>
                <span className="stat-label">Total Meals</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {meals.filter(m => m.meal_type === 'Break').length}
                </span>
                <span className="stat-label">Break Meals</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {meals.filter(m => m.meal_type === 'Lunch').length}
                </span>
                <span className="stat-label">Lunch Meals</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">
                  {meals.filter(m => m.meal_type === 'Snack').length}
                </span>
                <span className="stat-label">Snack Meals</span>
              </div>
            </div>

            <div className="students-actions">
              <button 
                className="add-student-btn"
                onClick={() => navigate('/school/add-meal')}
              >
                ➕ Add New Meal
              </button>
            </div>

            {loadingMeals ? (
              <div className="loading-container">
                <div className="loading-spinner">⏳</div>
                <p>Loading meals...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <div className="error-icon">❌</div>
                <p>{error}</p>
                <button 
                  className="retry-btn"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            ) : meals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🍽️</div>
                <h3>No Meals Found</h3>
                <p>
                  {filterType !== 'All' || filterFrequency !== 'All' 
                    ? 'No meals match your current filters. Try adjusting your filters.'
                    : 'You haven\'t added any meals yet. Start by adding your first meal!'}
                </p>
                {(!filterType || filterType === 'All') && (!filterFrequency || filterFrequency === 'All') && (
                  <button 
                    className="add-first-class-btn"
                    onClick={() => navigate('/school/add-meal')}
                  >
                    ➕ Add Your First Meal
                  </button>
                )}
              </div>
            ) : (
              <div className="students-table-container">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>Meal Name</th>
                      <th>Type</th>
                      <th>Frequency</th>
                      <th>Description</th>
                      <th>Date Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meals.map((meal) => (
                      <tr key={meal.id}>
                        <td className="student-name">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.25rem' }}>{getMealTypeIcon(meal.meal_type)}</span>
                            <strong>{meal.meal_name}</strong>
                          </div>
                        </td>
                        <td>{meal.meal_type}</td>
                        <td>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.375rem 0.75rem',
                            background: getFrequencyBadgeColor(meal.frequency) + '20',
                            color: getFrequencyBadgeColor(meal.frequency),
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                            {meal.frequency}
                          </span>
                        </td>
                        <td style={{ maxWidth: '300px' }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }} title={meal.description}>
                            {meal.description}
                          </div>
                        </td>
                        <td>{formatDate(meal.created_at)}</td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="edit-btn"
                              title="Edit Meal"
                              onClick={() => openEditPopup(meal)}
                              style={{
                                background: 'none',
                                color: '#FF8C42',
                                border: 'none',
                                padding: '0',
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                marginRight: '0.25rem'
                              }}
                            >
                              ✏️
                            </button>
                            <button
                              className="delete-btn"
                              title="Delete Meal"
                              onClick={() => handleDeleteMeal(meal.id, meal.meal_name)}
                              style={{
                                background: 'none',
                                color: '#FF8C42',
                                border: 'none',
                                padding: '0',
                                cursor: 'pointer',
                                fontSize: '1.2rem'
                              }}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Meal Popup */}
      {editingMeal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}
          onClick={closeEditPopup}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              marginTop: 0,
              marginBottom: '1.5rem',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700'
            }}>
              Edit Meal
            </h2>

            <form onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Meal Name *
                </label>
                <input
                  type="text"
                  name="meal_name"
                  value={editForm.meal_name}
                  onChange={handleEditInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: editErrors.meal_name ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter meal name"
                />
                {editErrors.meal_name && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {editErrors.meal_name}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Meal Type *
                </label>
                <select
                  name="meal_type"
                  value={editForm.meal_type}
                  onChange={handleEditInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: editErrors.meal_type ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select meal type</option>
                  <option value="Break">Break</option>
                  <option value="Lunch">Lunch</option>
                  <option value="Snack">Snack</option>
                </select>
                {editErrors.meal_type && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {editErrors.meal_type}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Frequency *
                </label>
                <select
                  name="frequency"
                  value={editForm.frequency}
                  onChange={handleEditInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: editErrors.frequency ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select frequency</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
                {editErrors.frequency && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {editErrors.frequency}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Description *
                </label>
                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleEditInputChange}
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: editErrors.description ? '2px solid #EF4444' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                  placeholder="Enter meal description"
                />
                {editErrors.description && (
                  <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {editErrors.description}
                  </span>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={closeEditPopup}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#FF8C42',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                >
                  {isSubmitting ? 'Updating...' : 'Update Meal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Meals;

